<template>
  <div class="station-form-category-container flex flex-col gap-y-2 pb-6">
    <span class="station-form-category-title font-bold">Comentários</span>

    <div class="flex flex-col gap-3">
      <div v-if="isLoading" class="text-gray-500 text-sm text-left">Carregando comentários...</div>

      <!-- Falha de carregamento não pode virar "Nenhum comentário registrado.": o
           estado de erro mostra a mensagem específica do service e oferece o retry. -->
      <div
        v-else-if="loadError"
        data-testid="comments-load-error"
        class="text-negative text-sm text-left flex items-center gap-2"
      >
        <span>{{ loadError }}</span>
        <button type="button" class="underline font-bold" @click="fetchComments">
          Tentar novamente
        </button>
      </div>

      <div v-else-if="localComments.length === 0" class="text-gray-500 text-sm text-left">
        Nenhum comentário registrado.
      </div>

      <div
        v-for="(comment, index) in localComments"
        :key="comment.id || index"
        class="bg-gray-100 p-3 rounded-lg flex justify-between items-start"
      >
        <div class="text-start">
          <p class="font-bold text-sm">
            {{ comment.author }}
            <span class="text-xs font-normal text-gray-500"> | {{ comment.date }} </span>
          </p>
          <p class="text-sm mt-1 text-left">{{ comment.text }}</p>
        </div>

        <v-tooltip v-if="canComment" text="Remover">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              color="error"
              variant="text"
              :icon="mdiTrashCanOutline"
              density="compact"
              size="small"
              :loading="deletingId === comment.id"
              @click="handleDelete(comment, index)"
            />
          </template>
        </v-tooltip>
      </div>
    </div>

    <div v-if="canComment" class="flex border rounded px-3 py-3 gap-4 items-start mt-2">
      <input
        v-model="draftComment"
        type="text"
        placeholder="Digite um comentário..."
        class="flex-grow border-none outline-none text-sm placeholder-gray-500 bg-transparent"
        @keyup.enter="handleSaveDraft"
      />

      <OutlineButton
        type="button"
        color="primary30"
        width="200px"
        height="32px"
        :disabled="!draftComment.trim()"
        :loading="isSaving"
        @click="handleSaveDraft"
      >
        Adicionar comentário
      </OutlineButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted, PropType } from 'vue'
import { format, subHours } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useToast } from 'vue-toastification'
import { mdiTrashCanOutline } from '@mdi/js'
import Network from '@/Network'
import { useErrorToast } from '@/composables/useErrorToast'
import { getApiErrorMessage } from '@/utils/apiError'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import { useUserStore } from '@/pinia-store/useUsersStore'

export interface CommentItem {
  id?: string | number
  author: string
  text: string
  date: string
}

const props = defineProps({
  comments: {
    type: Array as PropType<CommentItem[]>,
    default: () => []
  },
  currentUser: {
    type: String,
    default: undefined
  },
  attendanceId: {
    type: [Number, String] as PropType<number | string | undefined>,
    default: undefined
  },
  /**
   * Criar/remover comentário é escrita (o backend exige `attendance_emitting`).
   * `false` deixa os comentários só para leitura — caso do perfil Extrator.
   */
  canComment: {
    type: Boolean,
    default: true
  }
})

const toast = useToast()
const { notifyError } = useErrorToast()
const userStore = useUserStore()
const draftComment = ref('')
const localComments = ref<CommentItem[]>([...props.comments])
const isLoading = ref(false)
const loadError = ref<string | null>(null)
const isSaving = ref(false)
const deletingId = ref<string | number | undefined>(undefined)

function formatCommentDate(isoDate: string): string {
  try {
    // API retorna createdAt em UTC sem indicador de timezone; subtrai 3h para converter a UTC-3 (Brasília)
    return format(subHours(new Date(isoDate), 3), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
  } catch {
    return isoDate
  }
}

async function fetchComments() {
  if (!props.attendanceId) return
  isLoading.value = true
  loadError.value = null
  try {
    const response = await Network.dsAttendance.listComments(props.attendanceId)
    localComments.value = response.data.map((item) => ({
      id: item.id,
      author: item.author.name,
      text: item.text,
      date: formatCommentDate(item.createdAt)
    }))
  } catch (error: unknown) {
    const fallback = 'Não foi possível carregar os comentários.'
    loadError.value = getApiErrorMessage(error, fallback)
    notifyError(error, fallback)
  } finally {
    isLoading.value = false
  }
}

watch(
  () => props.comments,
  (newComments) => {
    if (!props.attendanceId) localComments.value = [...newComments]
  },
  { deep: true }
)

onMounted(() => {
  if (props.attendanceId) {
    fetchComments()
  }
})

const handleSaveDraft = async () => {
  const text = draftComment.value.trim()
  if (!text) return

  if (props.attendanceId) {
    isSaving.value = true
    try {
      await Network.dsAttendance.createComment(props.attendanceId, { text })
      draftComment.value = ''
      await fetchComments()
      toast.success('Comentário adicionado com sucesso!')
    } catch (error: unknown) {
      notifyError(error, 'Não foi possível adicionar o comentário.')
    } finally {
      isSaving.value = false
    }
    return
  }

  const currentDate = new Date()
    .toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
    .replace(',', ' às')

  localComments.value.unshift({
    id: Date.now(),
    author: props.currentUser ?? userStore.userData?.name ?? 'Usuário',
    text,
    date: currentDate
  })
  draftComment.value = ''
  toast.success('Comentário adicionado com sucesso!')
}

const handleDelete = async (comment: CommentItem, index: number) => {
  if (props.attendanceId && comment.id) {
    deletingId.value = comment.id
    try {
      await Network.dsAttendance.deleteComment(props.attendanceId, comment.id)
      localComments.value.splice(index, 1)
      toast.success('Comentário removido com sucesso!')
    } catch (error: unknown) {
      notifyError(error, 'Não foi possível remover o comentário.')
    } finally {
      deletingId.value = undefined
    }
    return
  }

  if (confirm('Tem certeza que deseja remover este comentário?')) {
    localComments.value.splice(index, 1)
    toast.success('Comentário removido com sucesso!')
  }
}
</script>
